"use client";

import React, { useState } from "react";
import Image from "next/image";
import type { BlockProps } from "./blocks/registry";

type StrapiMedia = {
  url?: string;
  alternativeText?: string;
};

type VideoItem = {
  id?: number | string;
  cover?: StrapiMedia;
  video?: StrapiMedia;
};

type VideoCarouselData = {
  id?: number | string;
  documentId?: string;
  titre?: string;
  subTitle?: string;
  video?: VideoItem[];
};

export default function VideoCarousel({ data }: BlockProps<VideoCarouselData>) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [direction, setDirection] = useState<"left" | "right" | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playingVideoUrl, setPlayingVideoUrl] = useState<string | null>(null);

  if (!data) return null;

  const { titre, subTitle } = data;
  const video = Array.isArray(data.video) ? data.video : [];

  if (video.length === 0) return null;

  const goToPrevious = () => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setDirection("right");

    setTimeout(() => {
      setCurrentIndex((prevIndex) =>
        prevIndex === 0 ? video.length - 1 : prevIndex - 1
      );
      setDirection(null);
    }, 250);

    setTimeout(() => {
      setIsTransitioning(false);
    }, 600);
  };

  const goToNext = () => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setDirection("left");

    setTimeout(() => {
      setCurrentIndex((prevIndex) =>
        prevIndex === video.length - 1 ? 0 : prevIndex + 1
      );
      setDirection(null);
    }, 250);

    setTimeout(() => {
      setIsTransitioning(false);
    }, 600);
  };

  const goToSlide = (index: number) => {
    if (isTransitioning || index === currentIndex) return;
    setIsTransitioning(true);
    const slideDirection = index > currentIndex ? "left" : "right";
    setDirection(slideDirection);

    setTimeout(() => {
      setCurrentIndex(index);
      setDirection(null);
    }, 250);

    setTimeout(() => {
      setIsTransitioning(false);
    }, 600);
  };

  const getPreviousIndex = () =>
    currentIndex === 0 ? video.length - 1 : currentIndex - 1;
  const getNextIndex = () =>
    currentIndex === video.length - 1 ? 0 : currentIndex + 1;

 const handlePlayVideo = () => {
  const videoUrl = video[currentIndex]?.video?.url;

  if (!videoUrl) return;

  setPlayingVideoUrl(videoUrl);
  setIsPlaying(true);
};

  const handleCloseVideo = () => {
    setIsPlaying(false);
    setPlayingVideoUrl(null);
  };

  return (
    <section key={data.id} className="bg-white py-10 px-10">
      <div className="mx-auto max-w-7xl">
        {/* Header */}
        <header className="text-center mb-10">
          <h2 className="text-4xl lg:text-[44px] font-bold text-slate-900 mb-4">
            {titre}
          </h2>
          <p className="text-xl md:text-2xl text-[#8CCB1C] font-medium">
            {subTitle}
          </p>
        </header>

        {/* Carousel Container */}
        <div className="relative">
          <div className="flex items-center justify-center gap-8 mb-12 relative">
            {/* Previous Video */}
            <div
              className={`hidden lg:block flex-shrink-0 w-64 opacity-40 hover:opacity-60 transition-all duration-500 ease-in-out cursor-pointer ${
                direction === "right"
                  ? "translate-x-16 scale-105"
                  : direction === "left"
                  ? "-translate-x-4 scale-95"
                  : ""
              }`}
            >
              <div
                className="relative aspect-video rounded-2xl overflow-hidden shadow-lg transition-transform duration-500 ease-in-out hover:scale-105"
                onClick={goToPrevious}
              >
                <Image
                  src={video[getPreviousIndex()].cover?.url || "/fallback-cover.jpg"}
                  alt="Previous video"
                  width={640}
                  height={605}
                />
              </div>
            </div>

            {/* Current Video */}
            <div
              className={`flex-shrink-0 w-full max-w-2xl transition-all duration-300 ease-out ${
                direction === "left"
                  ? "-translate-x-24 opacity-0"
                  : direction === "right"
                  ? "translate-x-24 opacity-0"
                  : "translate-x-0 opacity-100"
              }`}
            >
              <div
                className={`relative aspect-video rounded-3xl overflow-hidden shadow-2xl transition-all duration-300 ${
                  isTransitioning ? "scale-95" : "scale-100"
                }`}
              >
                <Image
                  src={video[currentIndex].cover?.url || "/fallback-cover.jpg"}
                  alt={video[currentIndex].cover?.alternativeText || ""}
                  width={1000}
                  height={614}
                />
                {/* Play Button */}
                <div className="absolute inset-0 flex items-center justify-center bg-black/10">
                  <button
                    onClick={handlePlayVideo}
                    className="w-20 h-20 bg-[#E21B44] rounded-2xl flex items-center justify-center hover:bg-[#c91839] transition-colors shadow-lg"
                    aria-label="Play video"
                  >
                    <svg
                      className="w-10 h-10 text-white ml-1"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M8 5v14l11-7z" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>

            {/* Next Video */}
            <div
              className={`hidden lg:block flex-shrink-0 w-64 opacity-40 hover:opacity-60 transition-all duration-500 ease-in-out cursor-pointer ${
                direction === "left"
                  ? "-translate-x-16 scale-105"
                  : direction === "right"
                  ? "translate-x-4 scale-95"
                  : ""
              }`}
            >
              <div
                className="relative aspect-video rounded-2xl overflow-hidden shadow-lg transition-transform duration-500 ease-in-out hover:scale-105"
                onClick={goToNext}
              >
                <Image
                  src={video[getNextIndex()].cover?.url || "/fallback-cover.jpg"}
                  alt="Next video"
                  width={1000}
                  height={614}
                />
              </div>
            </div>
          </div>

          {/* Controls + Dots */}
          <div className="flex items-center justify-center gap-4">
            <button
              onClick={goToPrevious}
              className="w-12 h-12 rounded-lg border-2 border-slate-300 flex items-center justify-center hover:border-[#E21B44] hover:text-[#E21B44] transition-all duration-300 hover:scale-110 disabled:opacity-50"
              disabled={isTransitioning}
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>

            <div className="flex items-center gap-2">
             {video.map((_, index) => (
                <button
                  key={index}
                  onClick={() => goToSlide(index)}
                  className={`w-3 h-3 rounded-full transition-all duration-300 ${
                    index === currentIndex
                      ? "bg-[#E21B44] w-8"
                      : "bg-slate-300 hover:bg-slate-400"
                  }`}
                  disabled={isTransitioning}
                />
              ))}
            </div>

            <button
              onClick={goToNext}
              className="w-12 h-12 rounded-lg border-2 border-slate-300 flex items-center justify-center hover:border-[#E21B44] hover:text-[#E21B44] transition-all duration-300 hover:scale-110 disabled:opacity-50"
              disabled={isTransitioning}
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>
          </div>
        </div>

        {/* Modal Player */}
        {isPlaying && playingVideoUrl && (
          <div
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 backdrop-blur-sm"
            onClick={handleCloseVideo}
          >
            <div className="relative w-full max-w-5xl mx-4">
              <button
                onClick={handleCloseVideo}
                className="absolute -top-12 right-0 text-white hover:text-secondary"
              >
                ✕
              </button>
              <div
                className="relative aspect-video rounded-2xl overflow-hidden shadow-2xl"
                onClick={(e) => e.stopPropagation()}
              >
                <video className="w-full h-full" controls autoPlay>
                  <source src={playingVideoUrl} type="video/mp4" />
                  Your browser does not support video.
                </video>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
